import rwcInternalApi, { intApiEdrClient, intApiFeatureFlagClient } from '../internal-api';
import { readLocallyStored, removeLocallyStored } from '../local-storage';
import {
    type JoinEventOutput,
    type JoinEventInput,
    type AnyJoinEvent,
    type JoinMeetingEndInputAttributes,
    type JoinPerfEventTypeValues,
    type JoinMeetingSourceType,
    type JoinIndirectCategoryType,
    type RcvMeetingAttributes,
    JoinPerfEventTypes,
    JoinMeetingSource,
    JoinDirectCategory,
    JoinIndirectCategory,
    JOIN_FLOW_DATA_KEY,
    JoinMeetingStartAttributes,
} from './join-flow-types';
import { getPrefixedLogger, prefixes } from '../rwc-logger';
import { uuidV4 } from '../deps-free/uuidV4';
import { ValuesOf } from '../types';
import { EDR_CHANNEL_RCV_IN_MEETING_EVENT } from '../edr-client/constants';
import { FEATURE_FLAGS } from '../constants/feature-flags';
import { createRequestTracker } from './join-flow-request-tracker';

const logger = getPrefixedLogger(prefixes.RWC_UTILS, 'analytics', 'join-flow-tracking');

const FLOW_ID_QUERY_PARAM = 'flow_id';
const SPAN_FLOW_ID_QUERY_PARAM = 'span_flow_id';
const JOIN_START_TS_QUERY_PARAM = 'join_meeting_start_ts';
const JOIN_SOURCE_QUERY_PARAM = 'join_source';

const locationSearch = window.location.search;
/** URL search params passed from Jupiter, parsed lazily */
let urlParams: URLSearchParams | null = null;

export const RCV_WEB_APP_NAME = 'rcv/web';

let currentSpanFlowId: string;
let spanIndirectDuration: number;
let currentFlowId: string;
/** is ready to track new events */
let isTerminated = false;

let meetingAttributes: RcvMeetingAttributes;
let joinEndAttributes: {
    durationMs: number;
    joinSource: JoinMeetingSourceType;
    embJwebVersion?: string;
};

const pendingInitEvents: Array<JoinEventInput> = [];
let startOperationsTimestamps: Map<StartEventTypes, number>;
let isMediaConnected = false;
/** a timestamp when audio selection modal is closed without joining audio */
let audioLeftUnjoinedTs: number | null = null;
let audioLeftUnjoinedSource: JoinMeetingSourceType | null = null;
let uuidFn: () => string;
// we have to track it because PreviewManager is mounted few times
let avPreviewEndEventSent = false;
/** can store JOIN_REQ or POST_MEETING while user is in waiting room */
let pendingJoinStartEvent: JoinEventOutput | null = null;
let currentIndirectCategory: JoinIndirectCategoryType | null = null;

const getCurrentTime = () => Date.now();

const initSpanFlow = (uuid = '') => {
    logger.debug('init span flow:', { uuid });
    currentSpanFlowId = uuid;
    meetingAttributes = {
        rcvBridgeId: '',
        rcvMeetingId: '',
        rcvSessionId: '',
    };
    // supplementary data for tracking
    spanIndirectDuration = 0;
    startOperationsTimestamps = new Map();
    isMediaConnected = false;
    audioLeftUnjoinedTs = null;
    audioLeftUnjoinedSource = null;
    avPreviewEndEventSent = false;
    pendingJoinStartEvent = null;
    currentIndirectCategory = null;
};

/** We don't reset currentFlowId here because some events can be sent after indirect join_meeting_end, but we need to track them with the same flow_id. */
const resetEndAttributes = () => {
    joinEndAttributes = {
        durationMs: 0,
        joinSource: '' as JoinMeetingSourceType,
    };
};

const getMeetingAttributes = (onlyMostRelevant: boolean) => {
    if (!onlyMostRelevant) return { ...meetingAttributes };

    const { rcvSessionId, rcvMeetingId, rcvBridgeId } = meetingAttributes;
    // By session id we can identify the meeting and bridge id. By meeting id - related bridge id.
    return (
        (rcvSessionId && { rcvSessionId }) ||
        (rcvMeetingId && { rcvMeetingId }) ||
        (rcvBridgeId && { rcvBridgeId }) ||
        null
    );
};

const setMeetingAttributes = (attributes: Partial<RcvMeetingAttributes>) => {
    if (isTerminated) return;

    logger.info('set meeting attributes:', { curr: meetingAttributes, new: attributes });
    meetingAttributes = { ...meetingAttributes, ...attributes };
};

const trackMediaConnectedWithoutAudio = (
    audioOptionsModalOpened: boolean,
    deviceError: string | null,
    endAttributes: JoinMeetingEndInputAttributes,
    timestamp?: number
) => {
    if (isTerminated || !currentSpanFlowId) return;

    isMediaConnected = true;
    const audioModalClosedByUser = audioLeftUnjoinedTs && audioLeftUnjoinedSource;
    const audioModalWasNotShown = !audioModalClosedByUser && !audioOptionsModalOpened;
    if (!audioModalClosedByUser && !audioModalWasNotShown) return; // continue only if audio is left unjoined intentionally

    // if audio wasn't shown at all, then JOIN_MEETING_START was already tracked and JOIN_MEETING_END did not happened.
    if (audioModalClosedByUser && audioLeftUnjoinedTs) {
        trackJoinFlow({
            eventType: JoinPerfEventTypes.JOIN_MEETING_START,
            ts: audioLeftUnjoinedTs,
            attributes: { joinSource: audioLeftUnjoinedSource as JoinMeetingSourceType },
        });
    }
    trackJoinFlow({
        eventType: JoinPerfEventTypes.JOIN_MEETING_END,
        attributes: {
            directCategory: JoinDirectCategory.RECV,
            error: Boolean(deviceError),
            errorCategory: deviceError || undefined,
            ...endAttributes,
        },
        ts: timestamp,
    });
};

const trackJoinPausedByAudioModal = (endAttributes: JoinMeetingEndInputAttributes, ts?: number) => {
    if (isTerminated || !currentSpanFlowId) return;

    trackJoinFlow({
        eventType: JoinPerfEventTypes.JOIN_MEETING_END,
        attributes: {
            indirectCategory: JoinIndirectCategory.JOIN_AUDIO_SELECTION,
            ...endAttributes,
        },
        ts,
    });
};

/**
 * @link "https://wiki.ringcentral.com/spaces/~artem.sokolov/pages/800187563/RCV+Join+Time+Measurements.+Structured+join+steps+reports#RCVJoinTimeMeasurements.Structuredjoinstepsreports-6.2.5Mediaconnectionestablishinghappensinparallelwithuser'smainjoinflow"
 */
const trackAudioLeftUnjoined = (
    joinSource: JoinMeetingSourceType,
    deviceError: string | null,
    endAttributes: JoinMeetingEndInputAttributes,
    ts?: number
) => {
    if (isTerminated || !currentSpanFlowId) return;

    if (!isMediaConnected) {
        audioLeftUnjoinedTs = ts || getCurrentTime();
        audioLeftUnjoinedSource = joinSource;
        return; // join_meeting_start/end will be tracked when media is connected
    }

    trackJoinFlow({
        eventType: JoinPerfEventTypes.JOIN_MEETING_START,
        ts,
        attributes: { joinSource },
    });
    trackJoinFlow({
        eventType: JoinPerfEventTypes.JOIN_MEETING_END,
        attributes: {
            directCategory: JoinDirectCategory.RECV,
            error: Boolean(deviceError),
            errorCategory: deviceError || undefined,
            ...endAttributes,
        },
        ts,
    });
};

const trackAudioJoined = (joinSource: JoinMeetingSourceType, timestamp?: number) => {
    if (!currentSpanFlowId) return;

    audioLeftUnjoinedTs = null;
    audioLeftUnjoinedSource = null;

    trackJoinFlow({
        eventType: JoinPerfEventTypes.JOIN_MEETING_START,
        ts: timestamp,
        attributes: { joinSource },
    });
};

initSpanFlow();
resetEndAttributes();

const EVENTS_END_START_PAIRS = {
    [JoinPerfEventTypes.JOIN_MEETING_END]: JoinPerfEventTypes.JOIN_MEETING_START,
    [JoinPerfEventTypes.WS_SIG_CREATE_RESP]: JoinPerfEventTypes.WS_SIG_CREATE_REQ,
    [JoinPerfEventTypes.WS_SIG_CONNECT_RESP]: JoinPerfEventTypes.WS_SIG_CONNECT_REQ,
    [JoinPerfEventTypes.PEER_CONN_EST_END]: JoinPerfEventTypes.PEER_CONN_EST_START,
} as const;

type StartEventTypes = ValuesOf<typeof EVENTS_END_START_PAIRS>;

const camelToSnakeCase = (str: string): string =>
    str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

const transformAttributes = <T extends Record<string, unknown>>(
    attrs: T
): Record<string, unknown> => {
    const transformed: Record<string, unknown> = {};
    Object.entries(attrs).forEach(([key, value]) => {
        transformed[camelToSnakeCase(key)] = value;
    });
    return transformed;
};

const transformEventKeys = (event: AnyJoinEvent): JoinEventOutput => {
    const result: JoinEventOutput = {
        event_type: event.eventType,
    };
    if (event.ts) {
        result.ts = event.ts;
    }
    if (event.attributes) {
        result.attributes = transformAttributes(event.attributes);
    }
    return result;
};

const composeJoinFlowEvent = (
    baseEvent: AnyJoinEvent,
    event: JoinEventInput,
    extraAttributes: Record<string, unknown>
): JoinEventOutput => {
    const nonEmptyExtras = Object.fromEntries(
        Object.entries(extraAttributes).filter(
            ([, value]) => value !== null && value !== undefined && value !== ''
        )
    );

    const attributes = {
        ...baseEvent.attributes,
        ...event.attributes,
        ...nonEmptyExtras,
    };

    return transformEventKeys({
        ...baseEvent,
        ...event,
        attributes,
    });
};

const checkStoredJoinFlowData = (): JoinMeetingSourceType => {
    const storedData = readLocallyStored(JOIN_FLOW_DATA_KEY);
    removeLocallyStored(JOIN_FLOW_DATA_KEY);
    const data = storedData as { joinSource: JoinMeetingSourceType } | null;
    if (data?.joinSource) {
        return data.joinSource;
    }
    return '' as JoinMeetingSourceType;
};

const isInitEvent = (eventType: JoinPerfEventTypeValues) =>
    eventType === JoinPerfEventTypes.JOIN_WEB_STATIC_INIT_START ||
    eventType === JoinPerfEventTypes.JOIN_WEB_STATIC_INIT_END ||
    eventType === JoinPerfEventTypes.JOIN_MEETING_START;

const isAVPreviewEndEvent = (event: JoinEventInput) =>
    event.eventType === JoinPerfEventTypes.JOIN_MEETING_END &&
    event.attributes?.indirectCategory === JoinIndirectCategory.AV_PREVIEW;

const isFlowInProgress = () => Boolean(joinEndAttributes.joinSource);

const createJoinFlowEvent = (
    event: JoinEventInput,
    generateUUID?: () => string
): JoinEventOutput[] => {
    const eventType = event.eventType;
    const skipAvPreviewEnd = isAVPreviewEndEvent(event) && avPreviewEndEventSent;
    const skipNonInitEvents = !currentSpanFlowId && !isInitEvent(eventType);

    logger.debug(`maybe create join flow event:`, event, 'state:', getState(), {
        skipAvPreviewEnd,
        skipNonInitEvents,
        isTerminated,
    });

    if (skipAvPreviewEnd || skipNonInitEvents || isTerminated) return [];

    /** base values captured before module state is changed */
    const baseEvent: Required<AnyJoinEvent> = {
        eventType,
        attributes: { flowId: currentFlowId, spanFlowId: currentSpanFlowId },
        ts: event.ts || getCurrentTime(),
    };
    const meetingAttrs = getMeetingAttributes(true);

    const composeEvent = (evt: JoinEventInput, attributes: Record<string, unknown> = {}) =>
        composeJoinFlowEvent(baseEvent, evt, { ...meetingAttrs, ...attributes });

    switch (eventType) {
        case JoinPerfEventTypes.JOIN_WEB_STATIC_INIT_START:
        case JoinPerfEventTypes.JOIN_WEB_STATIC_INIT_END:
            if (!currentFlowId) {
                pendingInitEvents.push({
                    ...event,
                    ts: baseEvent.ts,
                });
                return [];
            }
            return [composeEvent(event)];

        case JoinPerfEventTypes.JOIN_MEETING_START: {
            const joinSource = event.attributes?.joinSource;
            if (isFlowInProgress()) {
                logger.warn(
                    `JOIN_MEETING_START: flow already in progress (source: ${joinEndAttributes.joinSource}), skipping new source: ${joinSource}`
                );
                return [];
            }

            urlParams ||= new URLSearchParams(locationSearch);

            const storedJoinSource = checkStoredJoinFlowData();
            const flowIdFromUrl = urlParams.get(FLOW_ID_QUERY_PARAM);
            const urlJoinSource = urlParams.get(JOIN_SOURCE_QUERY_PARAM);
            const joinStartTsFromUrl = urlParams.get(JOIN_START_TS_QUERY_PARAM);
            const spanIdFromUrl = urlParams.get(SPAN_FLOW_ID_QUERY_PARAM);
            /** Flow started in Jupiter and continues in RWC */
            const isJupiterFlow = Boolean(
                flowIdFromUrl && spanIdFromUrl && joinStartTsFromUrl && urlJoinSource
            );
            const resJoinSource: JoinMeetingSourceType | null =
                storedJoinSource ||
                (isJupiterFlow && urlJoinSource) ||
                (joinSource === JoinMeetingSource.LINK_SENT && currentIndirectCategory) ||
                joinSource ||
                null;

            logger.debug(
                'create join_meeting_start:',
                { resJoinSource, joinSource, storedJoinSource, isJupiterFlow },
                '\n url params:',
                { flowIdFromUrl, spanIdFromUrl, joinStartTsFromUrl, urlJoinSource }
            );
            joinEndAttributes.joinSource = resJoinSource;

            if (generateUUID) {
                uuidFn = generateUUID;
            }

            if (uuidFn) {
                currentFlowId = (isJupiterFlow && flowIdFromUrl) || uuidFn();

                const extraEvents: JoinEventOutput[] = [];

                if (!currentSpanFlowId) {
                    initSpanFlow((isJupiterFlow && spanIdFromUrl) || uuidFn());

                    const hasInitEndEvent = pendingInitEvents.some(
                        pendingEvent =>
                            pendingEvent.eventType === JoinPerfEventTypes.JOIN_WEB_STATIC_INIT_END
                    );

                    for (const eventData of pendingInitEvents) {
                        extraEvents.push(...createJoinFlowEvent(eventData));
                    }

                    pendingInitEvents.length = 0;
                    if (hasInitEndEvent) {
                        extraEvents.push(
                            ...createJoinFlowEvent({
                                eventType: JoinPerfEventTypes.JOIN_WEB_STATIC_INIT_START,
                                ts: Math.round(performance.timeOrigin),
                            })
                        );
                    }

                    if (isJupiterFlow) {
                        joinEndAttributes.embJwebVersion =
                            urlParams.get('emb_jweb_version') || undefined;
                    }
                }

                startOperationsTimestamps.set(
                    JoinPerfEventTypes.JOIN_MEETING_START,
                    isJupiterFlow ? Number(joinStartTsFromUrl) : baseEvent.ts
                );

                urlParams.delete(FLOW_ID_QUERY_PARAM);
                urlParams.delete(JOIN_START_TS_QUERY_PARAM);
                urlParams.delete(JOIN_SOURCE_QUERY_PARAM);

                currentIndirectCategory = null;

                if (isJupiterFlow) {
                    return extraEvents;
                }

                const attributes: JoinMeetingStartAttributes = {
                    joinSource: resJoinSource,
                    app: RCV_WEB_APP_NAME,
                    flowId: currentFlowId,
                    spanFlowId: currentSpanFlowId,
                };

                const meetingStartEvent = composeEvent(event, attributes);

                return [...extraEvents, meetingStartEvent];
            }

            break;
        }
        case JoinPerfEventTypes.GET_BRIDGE_START:
        case JoinPerfEventTypes.POST_BRIDGE_START:
        case JoinPerfEventTypes.GET_BRIDGE_END:
        case JoinPerfEventTypes.POST_BRIDGE_END: {
            return [composeEvent(event)];
        }
        case JoinPerfEventTypes.POST_MEETING_START:
        case JoinPerfEventTypes.JOIN_REQ_START: {
            const isWaitingRoom = currentIndirectCategory === JoinIndirectCategory.WAITING_ROOM;
            if (isWaitingRoom) {
                pendingJoinStartEvent = composeEvent(event);
                return [];
            }

            return [composeEvent(event)];
        }
        case JoinPerfEventTypes.POST_MEETING_END:
        case JoinPerfEventTypes.JOIN_REQ_END: {
            const isError = Boolean(event.attributes?.error);
            const isWaitingRoom = currentIndirectCategory === JoinIndirectCategory.WAITING_ROOM;

            const pendingStart = pendingJoinStartEvent;
            pendingJoinStartEvent = null;

            if (isWaitingRoom && isError) return [];

            if (pendingStart) {
                return [pendingStart, composeEvent(event)];
            }

            return [composeEvent(event)];
        }
        case JoinPerfEventTypes.WS_SIG_CONNECT_REQ:
        case JoinPerfEventTypes.WS_SIG_CREATE_REQ:
        case JoinPerfEventTypes.PEER_CONN_EST_START: {
            startOperationsTimestamps.set(eventType, baseEvent.ts);
            return [composeEvent(event)];
        }
        case JoinPerfEventTypes.WS_SIG_CREATE_RESP:
        case JoinPerfEventTypes.WS_SIG_CONNECT_RESP:
        case JoinPerfEventTypes.PEER_CONN_EST_END: {
            const startTimestamp =
                startOperationsTimestamps.get(EVENTS_END_START_PAIRS[eventType]) || 0;
            const duration_ms = baseEvent.ts - startTimestamp;
            return [composeEvent(event, { duration_ms })];
        }
        case JoinPerfEventTypes.JOIN_MEETING_END: {
            const endAttrs = event.attributes;
            const isDirectCategory = Boolean(endAttrs?.directCategory);
            const startTimestamp =
                startOperationsTimestamps.get(EVENTS_END_START_PAIRS[eventType]) || 0;
            const durationMs = startTimestamp > 0 ? baseEvent.ts - startTimestamp : 0;

            const extraAttributes: Partial<JoinMeetingEndInputAttributes> = {
                ...joinEndAttributes,
                ...getMeetingAttributes(false),
                durationMs,
                app: RCV_WEB_APP_NAME,
            };

            if (isDirectCategory) {
                extraAttributes.spanFlowDurationMs = spanIndirectDuration + durationMs;
                currentIndirectCategory = null;
            } else {
                spanIndirectDuration += durationMs;
                currentIndirectCategory = endAttrs?.indirectCategory as JoinIndirectCategoryType;
            }

            avPreviewEndEventSent ||= isAVPreviewEndEvent(event);

            resetEndAttributes();

            const meetingEndEvent = composeEvent(event, extraAttributes);

            if (isDirectCategory) {
                cleanupSpanFlow('direct join_meeting_end');
            }

            return [meetingEndEvent];
        }
    }

    return [];
};

const cleanupJoinFlowEventTracker = (reason: string) => {
    resetEndAttributes();
    cleanupSpanFlow(reason);
    // allows new join flow to be initiated.
    isTerminated = false;
    joinFlowEventQueue.length = 0;
};

const cleaupJoinFlowEventTrackerIfNeeded = (reason: string) => {
    if (currentFlowId) {
        cleanupJoinFlowEventTracker(reason);
    }
};

const cleanupSpanFlow = (reason: string) => {
    logger.info('cleanup span flow:', reason);
    joinFlowRequestTracker.cleanup();
    initSpanFlow();
    // flow_id is reset either when the whole span is ended or when new JOIN_MEETING_START event is created.
    currentFlowId = '';
    isTerminated = true;
    urlParams?.delete(FLOW_ID_QUERY_PARAM);
    urlParams?.delete(SPAN_FLOW_ID_QUERY_PARAM);
    urlParams?.delete(JOIN_START_TS_QUERY_PARAM);
    urlParams?.delete(JOIN_SOURCE_QUERY_PARAM);
};

let joinFlowEventQueue: Array<JoinEventOutput> = [];

const dispatchJoinFlowEvent = (joinFlowEvent: JoinEventOutput) => {
    logger.info('dispatch join flow event:', joinFlowEvent);

    if (rwcInternalApi[intApiEdrClient]) {
        flushJoinFlowEventQueue();
        rwcInternalApi[intApiEdrClient].dispatchData(
            EDR_CHANNEL_RCV_IN_MEETING_EVENT,
            joinFlowEvent
        );
        return;
    }

    joinFlowEventQueue.push(joinFlowEvent);
};

const flushJoinFlowEventQueue = () => {
    if (!rwcInternalApi[intApiEdrClient] || joinFlowEventQueue.length === 0) {
        return;
    }

    logger.info('Flushing join flow event queue', joinFlowEventQueue);

    rwcInternalApi[intApiEdrClient].dispatchData(
        EDR_CHANNEL_RCV_IN_MEETING_EVENT,
        joinFlowEventQueue
    );
    joinFlowEventQueue = [];
};

const getIsJoinFlowAnalyticsEnabled = () =>
    rwcInternalApi[intApiFeatureFlagClient].getFlagV2(FEATURE_FLAGS.joinFlowAnalytics)?.enabled ??
    false;

export type TrackJoinFlow = typeof trackJoinFlow;

export const trackJoinFlow = (event: JoinEventInput) => {
    if (!getIsJoinFlowAnalyticsEnabled()) return;

    createJoinFlowEvent(event, uuidV4).forEach(dispatchJoinFlowEvent);
};

export const trackJoinFlowStart = (joinSource: JoinMeetingSourceType) =>
    trackJoinFlow({
        eventType: JoinPerfEventTypes.JOIN_MEETING_START,
        attributes: { joinSource },
    });

const joinFlowRequestTracker = createRequestTracker(trackJoinFlow, () =>
    Boolean(currentSpanFlowId)
);

/** For testing and debugging purposes only */
export const getState = () => ({
    currentSpanFlowId,
    spanIndirectDuration,
    currentFlowId,
    isTerminated,
    meetingAttributes,
    joinEndAttributes,
    isMediaConnected,
    audioLeftUnjoinedTs,
    audioLeftUnjoinedSource,
    avPreviewEndEventSent,
    pendingJoinReqStartEvent: pendingJoinStartEvent,
    currentIndirectCategory,
    joinFlowEventQueue,
    pendingData: joinFlowRequestTracker.getPendingData(),
    startOperationsTimestamps,
});

window[Symbol.for('joinFlow')] = { getState };

export {
    createJoinFlowEvent,
    cleanupJoinFlowEventTracker,
    cleaupJoinFlowEventTrackerIfNeeded,
    JoinPerfEventTypes,
    JoinMeetingSource,
    JoinIndirectCategory,
    JoinDirectCategory,
    JOIN_FLOW_DATA_KEY,
    joinFlowRequestTracker,
    trackMediaConnectedWithoutAudio,
    trackJoinPausedByAudioModal,
    trackAudioJoined,
    trackAudioLeftUnjoined,
    setMeetingAttributes,
};
